/**
 * #2261: the upload transport that can report how far it has got.
 *
 * `fetch` exposes no upload progress: a 60 MB site video sent with it is a
 * spinner from the first byte to the last, so a rep on a slow plant-floor
 * connection cannot tell a working upload from a stalled one. XMLHttpRequest
 * still carries `upload.onprogress`, which is the only way a browser tells us
 * bytes sent, so the two upload wrappers in lib/api.ts run through here.
 *
 * #2399: reporting bytes sent is only half of it. A connection that dies
 * without closing emits no progress, no error and no timeout, so the byte
 * counter freezes under a spinner and the queue waits for ever. The watchdog
 * below turns that silence into a failure the retry policy can act on, and an
 * AbortSignal lets the rep stop an upload they no longer want.
 *
 * The XHR is injectable so the progress contract can be tested without a DOM.
 */
import { makeApiError } from "./api-error";

/** The slice of XMLHttpRequest this module uses. */
export interface XhrLike {
  upload: { onprogress: ((e: { lengthComputable: boolean; loaded: number; total: number }) => void) | null };
  status: number;
  responseText: string;
  open(method: string, url: string): void;
  getResponseHeader(name: string): string | null;
  setRequestHeader(name: string, value: string): void;
  withCredentials: boolean;
  send(body: Document | XMLHttpRequestBodyInit | null): void;
  abort(): void;
  onload: (() => void) | null;
  onerror: (() => void) | null;
  onabort: (() => void) | null;
}

/**
 * #2424: there is no `timeout` here and no ontimeout handler, on purpose.
 * XMLHttpRequest.timeout is a ceiling on the WHOLE request, and a 60 MB video
 * on a plant-floor connection can honestly take twenty minutes, so any ceiling
 * short enough to be useful kills uploads the watchdog is deliberately keeping
 * alive. The stall window below catches the one fault a timeout would: bytes
 * that stop moving. Arming a timeout again means wiring a handler with it, or
 * the attempt hangs the way this pair of issues is about.
 */

export type XhrFactory = () => XhrLike;

/** Bytes sent of bytes to send, as the browser reports them. */
export type ProgressHandler = (sent: number, total: number) => void;

/**
 * #2399: how long the byte counter may sit still before the attempt is dead.
 * Long enough that a slow plant-floor connection sending a 60 MB video is
 * never cut off mid-transfer (a browser reports progress far more often than
 * once a minute while bytes are moving), short enough that a rep is not
 * watching a frozen spinner into their smoko.
 */
export const STALL_MS = 60_000;

/**
 * A stall is the client's own "took too long", so it carries 408: the retry
 * policy already reads that as worth another attempt AND pays it the 408/429
 * backoff rather than re-sending the whole file at once.
 */
export const UPLOAD_STALL_STATUS = 408;

/**
 * A cancel carries 499 (the client closed the request). The retry policy
 * already reads it as terminal, so nothing is sent again on its own, and the
 * rep's own Retry button still works if they change their mind.
 */
export const UPLOAD_CANCELLED_STATUS = 499;

export interface XhrUploadOptions {
  method: "POST" | "PUT";
  url: string;
  body: XMLHttpRequestBodyInit;
  headers?: Record<string, string>;
  onProgress?: ProgressHandler;
  /** Abort the bytes in flight. Fails the attempt with UPLOAD_CANCELLED_STATUS. */
  signal?: AbortSignal;
  /** Override the stall window. 0 or less turns the watchdog off. */
  stallMs?: number;
}

/**
 * One upload attempt. Resolves with the parsed JSON body, rejects with the
 * same ApiError shape the fetch wrappers throw. A transport fault (dropped
 * connection, timeout, abort) has no HTTP status at all, so it rejects with
 * status 0, which the retry rule reads as "worth trying again".
 */
export function xhrUpload<T>(opts: XhrUploadOptions, makeXhr: XhrFactory = defaultXhr): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    let watchdog: ReturnType<typeof setTimeout> | null = null;

    const stallMs = opts.stallMs ?? STALL_MS;
    const signal = opts.signal;

    const clearWatchdog = () => {
      if (watchdog !== null) clearTimeout(watchdog);
      watchdog = null;
    };

    /** Every exit runs through here, so a settled attempt leaves no timer
     *  ticking and no listener holding the form's controller alive. */
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearWatchdog();
      signal?.removeEventListener("abort", onSignalAbort);
      fn();
    };

    const failWith = (status: number, message: string) => settle(() => reject(makeApiError(status, message)));

    if (signal?.aborted) {
      // Cancelled before a single byte left the phone: send nothing.
      reject(makeApiError(UPLOAD_CANCELLED_STATUS, "cancelled"));
      return;
    }

    const xhr = makeXhr();

    /**
     * onabort cannot tell us WHY the request stopped, so the reason is
     * recorded here first and the abort follows. Both paths then fail the
     * attempt: a transport whose abort() never calls onabort must not leave
     * the promise pending, which is the very hang this issue is about.
     */
    let abortReason: { status: number; message: string } | null = null;
    const stop = (status: number, message: string) => {
      if (settled) return;
      // Recorded BEFORE abort(): a real XHR calls onabort from inside abort(),
      // so the handler must already know whether this was a stall or a cancel.
      abortReason = { status, message };
      try {
        xhr.abort();
      } catch {
        // A transport that cannot be stopped still fails the attempt.
      }
      failWith(status, message);
    };

    function onSignalAbort() {
      stop(UPLOAD_CANCELLED_STATUS, "cancelled");
    }

    /** Armed when the request is sent, and again on every byte reported. */
    const armWatchdog = () => {
      if (settled || !(stallMs > 0)) return;
      clearWatchdog();
      watchdog = setTimeout(() => {
        watchdog = null;
        stop(UPLOAD_STALL_STATUS, "stalled");
      }, stallMs);
    };

    xhr.open(opts.method, opts.url);
    xhr.withCredentials = true;
    for (const [name, value] of Object.entries(opts.headers ?? {})) xhr.setRequestHeader(name, value);

    const report = opts.onProgress;
    // Wired whether or not the caller wants the numbers: the watchdog needs
    // to hear the bytes even when nothing is drawing a bar.
    xhr.upload.onprogress = (e) => {
      armWatchdog();
      if (e.lengthComputable) report?.(e.loaded, e.total);
    };

    xhr.onerror = () => failWith(0, "network");
    xhr.onabort = () => failWith(abortReason?.status ?? 0, abortReason?.message ?? "aborted");

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        const err = withRetryAfter(makeApiError(xhr.status, xhr.responseText), xhr);
        settle(() => reject(err));
        return;
      }
      // An empty 204 is a valid answer for a poster upload nobody reads back.
      if (!xhr.responseText) {
        settle(() => resolve(undefined as T));
        return;
      }
      let parsed: T;
      try {
        parsed = JSON.parse(xhr.responseText) as T;
      } catch {
        failWith(xhr.status, xhr.responseText);
        return;
      }
      settle(() => resolve(parsed));
    };

    signal?.addEventListener("abort", onSignalAbort);
    xhr.send(opts.body);
    // A request that never moves a single byte stalls out too, so the first
    // window starts at send rather than at the first progress event.
    armWatchdog();
  });
}

/**
 * A 429 or a 503 can name the wait it wants, in seconds. The queue honours it
 * ahead of its own backoff, so the phone is not guessing at a number the
 * server already knows. An HTTP-date form is left alone: the delta-seconds
 * form is what these routes send, and a misread date would park the queue.
 */
function withRetryAfter(err: Error & { status: number }, xhr: XhrLike): Error {
  const header = safeHeader(xhr, "Retry-After");
  const seconds = header !== null && /^\d+$/.test(header.trim()) ? Number(header.trim()) : null;
  if (seconds !== null) (err as { retryAfterSeconds?: number }).retryAfterSeconds = seconds;
  return err;
}

function safeHeader(xhr: XhrLike, name: string): string | null {
  try {
    return xhr.getResponseHeader(name);
  } catch {
    return null;
  }
}

function defaultXhr(): XhrLike {
  return new XMLHttpRequest() as unknown as XhrLike;
}

/** No XHR (a non-browser runtime, a test environment without one) means the
 *  caller keeps its fetch path and simply reports no progress. */
export function canReportProgress(): boolean {
  return typeof XMLHttpRequest !== "undefined";
}
